import { Link } from 'react-router-dom';
import { DocSection, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'key-terms', label: 'Use key terms, not sentences' },
  { id: 'framing', label: 'Add shot & framing detail' },
  { id: 'quote-words', label: 'Quote exact words' },
  { id: 'exclude', label: "Exclude what you don't want" },
  { id: 'scope', label: 'Narrow the scope' },
  { id: 'ultra', label: 'Let Ultra combine signals' },
  { id: 'hide-results', label: 'Temporarily hide results' },
];

/* "Avoid / Better" query comparison — authored examples, not a screenshot of the app. */
function QueryCompare({ avoid, better }: { avoid: string; better: string }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, margin: '14px 0' }}>
      <div style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(255,84,104,0.25)', background: 'var(--danger-dim)' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.08em', color: 'var(--danger)' }}>AVOID</span>
        <p style={{ margin: '6px 0 0', fontSize: 13.5, lineHeight: 1.55, color: 'var(--fg-2)' }}>&ldquo;{avoid}&rdquo;</p>
      </div>
      <div style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(74,227,255,0.22)', background: 'var(--signal-dim)' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.08em', color: 'var(--signal-400)' }}>BETTER</span>
        <p style={{ margin: '6px 0 0', fontSize: 13.5, lineHeight: 1.55, color: 'var(--fg-2)' }}>&ldquo;{better}&rdquo;</p>
      </div>
    </div>
  );
}

export default function Body() {
  return (
    <>
      <DocSection id="key-terms" title="Use key terms, not sentences">
        <p>
          Better search starts with how you phrase the query. ClipRadar&rsquo;s{' '}
          <span style={kbd}>Visuals</span> mode is semantic — it reads the pixels in every scene
          rather than matching keywords against tags — so it responds best to the same short,
          concrete terms you&rsquo;d use to describe a shot to another editor, not a full sentence.
        </p>
        <QueryCompare
          avoid="A video where someone is walking down a street on a sunny day"
          better="person walking, street, daylight"
        />
        <p>
          The Search tab&rsquo;s own empty-state examples make the same point —{' '}
          <em>&ldquo;wide shot of nature&rdquo;</em>, <em>&ldquo;person talking&rdquo;</em>,{' '}
          <em>&ldquo;close up of hands&rdquo;</em> — nouns and a framing word, not prose.
        </p>
      </DocSection>

      <DocSection id="framing" title="Add shot & framing detail">
        <p>
          Visual search is sensitive to how a shot is framed, not only what&rsquo;s in it. Add a
          cinematography term — close-up, wide shot, over-the-shoulder — alongside the subject to
          land on the right scene faster.
        </p>
        <QueryCompare avoid="hands" better="close-up of hands" />
      </DocSection>

      <DocSection id="quote-words" title="Quote exact words">
        <p>
          Switch to <span style={kbd}>Speech</span> mode to search what people actually said. It
          searches the full text of every transcript, so it rewards the exact words a speaker
          used — a direct quote — over a paraphrase of the topic. See{' '}
          <Link to="/docs/concepts/speech-search">Speech search</Link> for how transcription and
          matching work.
        </p>
        <QueryCompare avoid="the part about funding problems" better="we need more runway" />
      </DocSection>

      <DocSection id="exclude" title="Exclude what you don't want">
        <p>
          The <strong>Exclude</strong> filter next to the search box removes matches you don&rsquo;t
          want — a person, a place, a repeated shot — without rewriting the whole query. It works
          alongside <span style={kbd}>Visuals</span> and <span style={kbd}>Speech</span>; under
          Ultra it&rsquo;s replaced by an equivalent <strong>Not</strong> chip instead.
        </p>
      </DocSection>

      <DocSection id="scope" title="Narrow the scope">
        <p>
          The <strong>Scope</strong> filter, top-left of the search box, limits a search to a
          collection, a folder, or a selection of files instead of your whole library. In a large
          library this is usually the fastest way to cut down noise — and it&rsquo;s the one filter
          that stays active even while <span style={kbd}>Ultra</span> is on.
        </p>
        <DocShot slug="search-best-practices" shot="scope" alt="The scope filter open above the search box" caption="Scope narrows a search to a collection, folder, or selection before you even type." />
      </DocSection>

      <DocSection id="ultra" title="Let Ultra combine signals">
        <p>
          When a search needs more than one signal at once — a person, a phrase they said, and how
          the shot is framed — stop juggling <span style={kbd}>Visuals</span>,{' '}
          <span style={kbd}>Speech</span>, and the People filter separately. Toggle the{' '}
          <span style={kbd}>Ultra</span> pill (or type <span style={kbd}>/find</span>) and describe
          the moment in one sentence — Ultra parses it into all of those facets and searches every
          engine at once. See <Link to="/docs/concepts/ultra-search">Ultra Search</Link> for how it
          works and what it needs from your GPU.
        </p>
      </DocSection>

      <DocSection id="hide-results" title="Temporarily hide results">
        <p>
          Not every match belongs in the batch — a scene that&rsquo;s technically similar but wrong
          for the edit. Right-click a result card and choose <strong>Remove from results&hellip;</strong>{' '}
          to clear it from the current results list without touching the file or your library.
          Nothing is deleted; it&rsquo;s just out of your way for this search.
        </p>
      </DocSection>
    </>
  );
}
